import {
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ICellEditorParams,
  RowNode,
} from '@ag-grid-community/core'
import { HttpParams } from '@angular/common/http'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import {
  RootState,
  Session,
  selectActiveSession,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectActiveUserId,
  selectActiveWindowDate,
  setCommandsMetrics,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams, resizeColumns } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  InlineEditorParams,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter } from 'date-fns'
import { Subject } from 'rxjs'
import { debounceTime, retry, take, takeUntil } from 'rxjs/operators'
import { CounterParty } from '../../../model/counter-party.model'
import { Currency } from '../../../model/currency.model'
import { AccountDetails } from '../../../model/single-transaction/account-details.model'
import { CreateEditTransactionRequest } from '../../../model/single-transaction/create-edit-transaction.model'
import { ExternalTransactionDetail } from '../../../model/single-transaction/external-transaction-detail'
import { ExternalUnmatchedTransaction } from '../../../model/single-transaction/external-unmatched-transaction'
import {
  FXMappingModel,
  FXSpot,
  fxField,
} from '../../../model/single-transaction/fx-spot-trade/fx-spot.model'
import { TransactionModalInput } from '../../../model/single-transaction/popup-input.model'
import { SleeveSimpleAccounts } from '../../../model/single-transaction/sleeve-simple-accounts.model'
import { TransactionDetail } from '../../../model/single-transaction/transaction-detail'
import {
  FXValidationView,
  ValidationExternalTransactionData,
} from '../../../model/single-transaction/validation-view.model'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import { PasteService } from '../../../services/single-transaction/paste.service'
import {
  ACCOUNT_REQUIRED_ERROR,
  ACCOUNT_REQUIRED_FIELD_WARNING,
  SINGLE_DIALOG_EVENT_NAME,
  SINGLE_DIALOG_VALIDATION_EVENT_NAME,
  activateDialog,
  addWarningDetails,
  clearAllInvalidations,
  createMetadata,
  emptySelectedCellsOnValidKeyPress,
  fillHandleDoubleClickHandlerSingleDialog,
  focusFieldRowEditor,
  formatNumberWithThousandSeparator,
  getCounterPartyFromName,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  getStringISOForDate,
  handleValidation,
  isDialogComponentInActive,
  singleDialogMetricsDetail,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { getCurrencyFromCurrencyCode } from '../../../util/currency-utils'
import {
  generateFxSpotCreateMapping,
  generateFxSpotEditMapping,
  generateFxSpotExternalMapping,
} from '../../../util/fx-spot-data-mapper-util'
import {
  isEditModal,
  isExternalTransaction,
} from '../../../util/popup-input-util'
import { getRequestModeFromModalMode } from '../../../util/validation-response-util'
import { TransactionTypeLabel } from '../../../model/single-transaction/transaction'

@Component({
  selector: 'hs-fx-spot-trade',
  templateUrl: './fx-spot-trade.component.html',
  styleUrls: ['./fx-spot-trade.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PasteService, CopyService],
})
export class FxSpotTradeModalComponent implements OnInit, OnDestroy {
  // True while an aggregate account is selected but no concrete sub-account has been
  // picked yet. Fields stay editable; validation is deferred and edited fields are flagged.
  aggregateAccountUnresolved = false
  @Input() transInput?: TransactionModalInput

  gridApi?: AgGridApi

  getFieldValidatorFormatter = getFieldValidatorFormatter
  transactionLoaded = false
  accountDetails?: AccountDetails
  rowNodeMap = new Map<string, RowNode>()
  formSubmitting = false
  forbiddenErrors = new FormControl('')
  rowData: FXMappingModel[] | null = null
  sessionId?: string
  accountId?: number
  sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  userId?: number
  reconDate?: Date
  isSettleDateFuture = false
  windowDate?: Date
  originalTransaction?: FXSpot
  transaction?: FXSpot | ExternalUnmatchedTransaction
  executionId?: number
  externalAccountId?: number
  isExternalAccountSleeveSimple = false
  externalTranTypeLabel?: TransactionTypeLabel
  originalCusip?: string
  errorFields: string[] = []
  isDataValid = false
  isCellEditing = false
  isValidating = false
  activeSession?: Session
  noteWarning: ValidationErrors | null = null
  counterPartyList: CounterParty[] = []
  currencyList: Currency[] = []
  compId = 'fxSpot-' + Math.random()
  activeInstances: NgbModalRef[] = []
  title = 'Create FX Trade'
  note = new FormControl('')
  clearTransaction = new FormControl(false)

  private readonly colDefs: ColDef[] = this.createColumnDefs()
  gridOptions: GridOptions = this.createGridOptions()

  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorDetails: unknown
  errorHeader: string | undefined
  private readonly destroySubject$ = new Subject()

  constructor(
    public activeModal: NgbActiveModal,
    private readonly modalService: NgbModal,
    public toastService: ToastService,
    public createEditTransactionService: CreateEditTransactionService,
    readonly store: Store<RootState>,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
    private readonly telemetryService: TelemetryService,
  ) {
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
      })
    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.accountId = session?.loadedAccountIds[0]
      })
    this.store
      .select(selectActiveUserId)
      .pipe(take(1))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveWindowDate)
      .pipe(take(1))
      .subscribe(windowDate => {
        this.windowDate = windowDate
      })
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()

    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  async ngOnInit() {
    const startTime = Date.now()
    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.createEditTransactionService
      .loadCounterParties()
      .subscribe(counterparties => {
        this.counterPartyList = counterparties
      })
    this.rowData = generateFxSpotCreateMapping(
      this.reconDate
        ? this.reconDate.toString()
        : getStringISOForDate(new Date()),
    )
    this.clearTransaction.patchValue(isEditModal(this.transInput))
    if (isEditModal(this.transInput)) this.clearTransaction.disable()

    if (this.transInput && isEditModal(this.transInput)) {
      this.title = this.transInput.readOnly ? 'View FX Trade' : 'Edit FX Trade'
      await this.plugEditFXData(
        this.sessionId,
        this.transInput?.transactionId,
        this.accountId,
        this.transInput.securityId,
      )
    } else {
      if (isExternalTransaction(this.transInput) && this.accountId) {
        this.title = 'Post FX Trade'
        await this.plugExternalUnmatchedDataView(
          Number(this.transInput?.transactionId),
          this.accountId,
        )
      } else {
        this.isDataValid = false
        this.setupInitialData(this.rowData)
        this.transactionLoaded = true
        this.createEditTransactionService.loadCurrencies().subscribe(
          currencies => {
            this.currencyList = currencies
          },
          err => {
            this.errorHeader = 'Error Loading Currencies'
            this.errorDetails = err.error
          },
        )
        if (this.accountId) {
          this.createEditTransactionService
            .loadAccountDetails(this.accountId)
            .subscribe(
              accountDetails => {
                this.accountDetails = accountDetails
                this.setAccountDisplayName()
                this.validate()
              },
              err => {
                this.errorHeader = TransactionConstants.ERROR_LOADING_ACCOUNT
                this.errorDetails = err.error
              },
            )
        }
      }
    }
    const now = Date.now()
    const type = singleDialogMetricsDetail(this.transInput)
    this.telemetryService.emitCustomEvent(SINGLE_DIALOG_EVENT_NAME, {
      dialogName: 'Fx-Spot-' + type,
      durationMs: now - startTime,
    })

    this.note.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.validate()
    })
    this.clearTransaction.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.validate()
    })
    this.unclearTransactionInitially()
  }

  plugExternalUnmatchedDataView(
    extTranId: number,
    accountId: number,
  ): Promise<[ExternalTransactionDetail, Currency[]]> {
    const apiPromise = this.getExternalPromises(extTranId, accountId)
    apiPromise
      .then(async ([externalTransactionDetail, currencies]) => {
        this.currencyList = currencies
        const externalTransactionData =
          externalTransactionDetail.externalTransaction
        this.executionId = externalTransactionData.executionId
        this.externalAccountId = externalTransactionData.accountId
        this.externalTranTypeLabel =
          externalTransactionData.transactionTypeLabel
        this.originalCusip =
          externalTransactionData.cusip ?? externalTransactionData.alternateId
        this.transaction = externalTransactionData
        this.accountDetails = externalTransactionDetail.accountDetails
        if (
          this.accountDetails?.isAggregate &&
          this.accountDetails.accountId != this.externalAccountId
        ) {
          this.isExternalAccountSleeveSimple = true
          this.fetchSleeveSimples(this.accountDetails.accountId)
          await this.loadAndMapAccountDetailsForSleeve(this.externalAccountId)
        }

        if (
          externalTransactionData.clearwaterCounterParty &&
          !this.counterPartyList.find(
            cp =>
              cp.name == externalTransactionData.clearwaterCounterParty?.name,
          )
        ) {
          this.counterPartyList.push(
            externalTransactionData.clearwaterCounterParty,
          )
        }
        this.rowData = generateFxSpotExternalMapping(
          externalTransactionData,
          currencies,
          this.isExternalAccountSleeveSimple,
        )
        this.setupInitialData(this.rowData)
        this.setAccountDisplayName()
        this.transactionLoaded = true
        this.validate()
      })
      .catch(err => {
        this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
        this.errorDetails = err.error
      })
    return apiPromise
  }

  async plugEditFXData(
    sessionId?: string,
    transactionId?: string,
    accountId?: number,
    securityId?: number,
  ): Promise<[TransactionDetail, Currency[]] | undefined> {
    if (transactionId && sessionId && accountId && securityId) {
      const params = new HttpParams()
        .set('accountId', accountId.toString())
        .set('securityId', securityId.toString())
      const apiPromise = this.getEditPromises(sessionId, transactionId, params)
      apiPromise
        .then(async ([trans, currencies]) => {
          this.note.setValue(trans.note?.note ?? '')
          const spotTrade = trans.transactionData as FXSpot
          this.clearTransaction.patchValue(spotTrade.clearedDate ? true : false)
          this.currencyList = currencies

          this.accountDetails = trans.accountDetails
          this.originalTransaction = spotTrade
          this.transaction = spotTrade

          if (
            spotTrade.counterParty &&
            !this.counterPartyList.find(
              cp => cp.name == spotTrade.counterParty?.name,
            )
          ) {
            this.counterPartyList.push(spotTrade.counterParty)
          }

          this.rowData = generateFxSpotEditMapping(spotTrade, currencies)
          if (this.accountDetails?.isAggregate) {
            await this.loadAndMapAccountDetailsForSleeve(spotTrade.accountId)
          }
          this.setupInitialData(this.rowData)
          this.transactionLoaded = true
          this.setAccountDisplayName()
          this.validate()
        })
        .catch(err => {
          this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
          this.errorDetails = err.error
        })
      return apiPromise
    }
    return undefined
  }

  get colDef() {
    return this.colDefs
  }

  private createColumnDefs(): ColDef[] {
    return [
      {
        field: fxField('isEditable'),
        hide: true,
      },
      {
        field: fxField('errorMessage'),
        hide: true,
      },
      {
        field: fxField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: params => {
          return params.data.validityStatus === 'valid'
            ? 'mdi mdi-check-circle text-success'
            : params.data.validityStatus === 'invalid'
            ? 'mdi mdi-close-circle-outline text-danger'
            : 'mdi mdi-alert-circle-outline text-warning'
        },
        tooltipField: 'errorMessage',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessage != null && params.data.errorMessage != '',
        } as NfTooltipParams,
        maxWidth: heliosDefaultColWidth.ValidityStatusCol,
      },
      {
        field: fxField('field'),
        headerName: 'Field',
        maxWidth: 100,
      },
      {
        field: fxField('value'),
        headerName: 'Value',
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        editable: (x: EditableCallbackParams) => this.editable(x),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
    ]
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: event => {
        this.gridApi = event
        resizeColumns(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.pasteService.registerGrid(event.api, event.columnApi)
      },
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      suppressMultiRangeSelection: false,
      enableFillHandle: true,
      fillHandleDirection: 'y',
      onCellEditingStarted: () => {
        this.isCellEditing = true
      },
      onCellEditingStopped: () => {
        this.isCellEditing = false
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
          const categoryNode = this.rowNodeMap.get('tranType')
          if (categoryNode?.data.value == '') {
            handleValidation(categoryNode, 'INVALID', 'Valid Value Required')
            this.isDataValid = false
          }
        }
      },
      getRowNodeId: ({ id }: FXMappingModel) => id,
      columnDefs: this.colDefs,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFirstDataRendered: (params: any) => {
        params.api.sizeColumnsToFit()
      },
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandlerSingleDialog(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: event.rowIndex ?? 0,
            colKey: event.column.getColId(),
          })
        }
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
    }
  }

  editable(params: EditableCallbackParams): boolean {
    const tableNode = params.api?.getRowNode(params.data['id'])
    if (tableNode) {
      this.rowNodeMap.set(params.data['id'], tableNode)
    }
    // Fields stay editable before an account is picked; validation is deferred instead, and
    // edited rows are flagged until the account is resolved (see onColumnValueChanged).
    return params.data.isEditable
  }

  /* Define dataType for all fields */
  editInlineColumns(params: ICellEditorParams): InlineEditorParams {
    switch (params.data.id) {
      case 'settleDate':
      case 'entryDate':
        return {
          dataType: InlineEditDataType.Date,
        }
      case 'fromUnits':
      case 'toUnits':
      case 'sellToBuyUnitRatio':
      case 'buyToSellUnitRatio':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-16',
          },
          disablePopover: true,
        }
      case 'fromSecurityId':
      case 'toSecurityId':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.currencyList,
            bindValue: 'code',
            bindLabel: 'code',
            loading: this.currencyList.length == 0,
          },
        }
      case 'counterParty':
        return {
          dataType: InlineEditDataType.Select,
          options: this.getCounterPartyOptions(),
        }
      case 'tranType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: TransactionConstants.FX_SPOT_OPTIONS,
            bindValue: 'label',
            bindLabel: 'label',
          },
        }
      case 'account':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.sleeveSimpleAccount,
            bindValue: 'displayLabel',
            bindLabel: 'displayLabel',
            loading: this.sleeveSimpleAccount.length == 0,
          },
        }
      default:
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
    }
  }

  onColumnValueChanged(event: CellValueChangedEvent) {
    if (
      event.newValue === event.oldValue ||
      event.column.getColId() != 'value'
    ) {
      return
    }
    const fxData: FXMappingModel = event.data
    if ('account' != fxData.id) {
      if (this.aggregateAccountUnresolved) {
        // No account yet: flag the edited field instead of validating it.
        addWarningDetails(event.node, ACCOUNT_REQUIRED_FIELD_WARNING)
        event.api.refreshCells({ force: true, rowNodes: [event.node] })
      } else {
        this.validate()
      }
    }

    switch (fxData.id) {
      case 'toSecurityId':
      case 'fromSecurityId':
        this.adjustForCurrencyData(event)
        break
      case 'toUnits':
      case 'fromUnits':
        this.adjustForUnitsData(event)
        break
      case 'account':
        this.loadAggAccountDetails(event.data['value'])
        break
      case 'settleDate':
        this.clearUnclearTransaction(event.data['value'])
        break
    }
  }

  adjustForCurrencyData(event: CellValueChangedEvent) {
    if (event.data['value']) {
      if (event.data['id'] == 'fromSecurityId') {
        this.rowNodeMap
          .get('fromUnits')
          ?.setDataValue('field', 'Sell : ' + event.data['value'])
      } else {
        this.rowNodeMap
          .get('toUnits')
          ?.setDataValue('field', 'Buy : ' + event.data['value'])
      }
    }
  }

  adjustForUnitsData(event: CellValueChangedEvent) {
    const currentValue = event.data['value']
    const toUnits = this.rowNodeMap.get('toUnits')?.data.value
    const fromUnits = this.rowNodeMap.get('fromUnits')?.data.value
    if (currentValue) {
      if (event.data['id'] == 'fromUnits' && toUnits != '') {
        this.rowNodeMap
          .get('sellToBuyUnitRatio')
          ?.setDataValue(
            'value',
            (Number(currentValue) / Number(toUnits)).toString(),
          )
        this.rowNodeMap
          .get('buyToSellUnitRatio')
          ?.setDataValue(
            'value',
            (Number(toUnits) / Number(currentValue)).toString(),
          )
      } else if (event.data['id'] == 'toUnits' && fromUnits !== '') {
        this.rowNodeMap
          .get('sellToBuyUnitRatio')
          ?.setDataValue(
            'value',
            (Number(fromUnits) / Number(currentValue)).toString(),
          )
        this.rowNodeMap
          .get('buyToSellUnitRatio')
          ?.setDataValue(
            'value',
            (Number(currentValue) / Number(fromUnits)).toString(),
          )
      }
    }
  }

  clearUnclearTransaction(date: string) {
    if (
      isAfter(
        new Date(date),
        this.reconDate
          ? new Date(this.reconDate as unknown as string)
          : new Date(),
      )
    ) {
      this.clearTransaction.patchValue(false)
    } else {
      this.clearTransaction.patchValue(true)
    }
  }

  unclearTransactionInitially() {
    const settleDate =
      this.rowData?.find(row => row.id == 'settleDate')?.value ?? ''
    this.isSettleDateFuture = isAfter(
      new Date(settleDate),
      this.reconDate
        ? new Date(this.reconDate as unknown as string)
        : new Date(),
    )
    if (this.isSettleDateFuture) {
      this.clearTransaction.patchValue(false)
    } else {
      this.clearTransaction.patchValue(true)
    }
  }

  validate() {
    const startTime = Date.now()
    const categoryNode = this.rowNodeMap.get('tranType')
    if (!categoryNode?.data.value) {
      return
    }
    // Don't validate while an aggregate account is unresolved; there's no concrete
    // account to scope the request to yet (edited fields are flagged instead).
    if (this.aggregateAccountUnresolved) {
      return
    }
    this.isDataValid = false
    const validateView = this.createValidationView(
      this.accountDetails as AccountDetails,
    )
    this.isValidating = true
    this.createEditTransactionService
      .validateTransaction(validateView, TranCategoryUrl.FX_TRADE)
      .subscribe(
        validationInfos => {
          this.handleValidationResponse(
            validationInfos.filter(
              validationInfo => validationInfo.level != ErrorSeverity.FORBIDDEN,
            ),
          )
          this.handleValidationResponseForbidden(
            validationInfos.filter(
              validationInfo => validationInfo.level == ErrorSeverity.FORBIDDEN,
            ),
          )
          this.isValidating = false
          const now = Date.now()
          const type = singleDialogMetricsDetail(this.transInput)
          this.telemetryService.emitCustomEvent(
            SINGLE_DIALOG_VALIDATION_EVENT_NAME,
            {
              dialogName: 'Fx-Spot-' + type,
              durationMs: now - startTime,
            },
          )
        },
        err => {
          this.errorHeader = TransactionConstants.VALIDATION_ERROR
          this.errorDetails = err.error
          this.isValidating = false
        },
      )
  }

  handleValidationResponseForbidden(validationInfos: ValidationInfo[]) {
    if (validationInfos.length == 0) return
    const forbiddenErrorsList: string[] = []
    validationInfos.forEach(validationInfo => {
      forbiddenErrorsList.push(validationInfo.message)
    })
    const validatorKey = 'forbiddenErrorsValidator'
    this.forbiddenErrors.markAsTouched()
    this.forbiddenErrors.setErrors({
      [validatorKey]: {
        message: forbiddenErrorsList,
      },
    })
    this.isDataValid = false
  }

  handleValidationResponse(validationInfos: ValidationInfo[]) {
    const errorMessages: Map<string, string[]> = new Map()
    const warningMessages: Map<string, string[]> = new Map()
    validationInfos.forEach(validationInfo => {
      if (validationInfo.level == ErrorSeverity.ERROR) {
        validationInfo.impactedFields.forEach(impactedField => {
          if (errorMessages.has(impactedField)) {
            errorMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            errorMessages.set(impactedField, [validationInfo.message])
          }
        })
      } else {
        validationInfo.impactedFields.forEach(impactedField => {
          if (warningMessages.has(impactedField)) {
            warningMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            warningMessages.set(impactedField, [validationInfo.message])
          }
        })
      }
    })
    this.isDataValid = errorMessages.size == 0
    this.rowNodeMap.forEach((value: RowNode) => {
      handleValidation(value, 'VALID', '')
    })
    this.note.setErrors(null)
    errorMessages.forEach((value: string[], key: string) => {
      let currentNode = this.rowNodeMap.get(key)
      if (key == 'accountId') currentNode = this.rowNodeMap.get('account')
      if (currentNode) {
        handleValidation(currentNode, 'INVALID', value.join(' | '))
      } else if (key == 'transactionNote') {
        const validatorKey = 'transactionNoteValidator'
        this.note.markAsTouched()
        this.note.setErrors({
          [validatorKey]: {
            message: value,
          },
        })
      }
    })
    warningMessages.forEach((value: string[], key: string) => {
      let currentNode = this.rowNodeMap.get(key)
      if (key == 'accountId') currentNode = this.rowNodeMap.get('account')
      if (currentNode && currentNode.data.errorMessage == '') {
        handleValidation(currentNode, 'WARNING', value.join(' | '))
      } else if (
        key == 'transactionNote' &&
        !errorMessages.has('transactionNote')
      ) {
        const validatorKey = 'transactionNoteValidator'
        this.noteWarning = {
          [validatorKey]: {
            message: value,
          },
        }
      }
    })
  }

  createValidationView(accountDetails: AccountDetails): FXValidationView {
    const view: FXValidationView = {
      transaction: this.prepareTransactionData(true),
      accountDetails: accountDetails,
      requestMode: getRequestModeFromModalMode(this.transInput),
      windowDate: this.windowDate ?? new Date(),
    }
    if (this.note.value != '') {
      view.transactionNote = this.note.value
    }
    if (this.externalAccountId) {
      view.externalTransaction = {
        accountId: this.externalAccountId,
        isSleeveSimple: this.isExternalAccountSleeveSimple,
      } as ValidationExternalTransactionData
    }
    return view
  }

  setupInitialData(rowData: FXMappingModel[] | null) {
    rowData?.forEach(element => {
      if (element.validityStatus === 'invalid') {
        this.errorFields.push(element.id)
      }
      this.isDataValid = false
      const tableNode = this.rowNodeMap.get(element.id)

      if (tableNode) {
        if (
          tableNode.data.validityStatus == 'invalid' &&
          element.validityStatus == 'valid'
        ) {
          this.errorFields.splice(
            this.errorFields.indexOf(tableNode.data['id']),
            1,
          )
          if (this.errorFields.length == 0) {
            this.isDataValid = true
          }
        }
        tableNode.data = element
        this.gridApi?.api.redrawRows({ rowNodes: [tableNode] })
      }
    })
  }

  save() {
    if (!this.isDataValid || !this.sessionId) {
      return
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'fxSpotTrade',
        time: Date.now(),
      }),
    )
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    let unclearTransaction = false
    if (this.originalTransaction?.clearedDate && !this.clearTransaction.value) {
      unclearTransaction = true
    }
    const tranCommand: CreateEditTransactionRequest = {
      transaction: this.prepareTransactionData(false),
      tranCategory: TranCategory.FX_TRADE,
      metadata: createMetadata(
        this.reconDate ?? new Date(),
        this.rowNodeMap.get('toSecurityId')?.data.value,
        unclearTransaction,
      ),
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }
    if (this.originalTransaction?.transactionId) {
      tranCommand.notes = TransactionConstants.HELIOS_FX_TRADE_SAVE_NOTE_CW_EDIT
      this.createEditTransactionService
        .editTransaction(
          this.sessionId,
          this.originalTransaction.transactionId,
          tranCommand,
        )
        .subscribe(
          () => {
            this.toastService.success(
              'Clearwater Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            this.closeModal()
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      if (isExternalTransaction(this.transInput)) {
        tranCommand.notes =
          TransactionConstants.HELIOS_FX_TRADE_SAVE_NOTE_EXTERNAL
        tranCommand.extTran = {
          externalAccountId: this.externalAccountId ?? 0,
          executionId: this.executionId ?? 0,
          externalTranId: Number(this.transInput?.transactionId),
          originalCusip: this.originalCusip,
          status: this.transInput?.externalTransactionStatus,
        }

        if (this.externalTranTypeLabel?.id) {
          tranCommand.transaction.typeLabel = {
            id: this.externalTranTypeLabel.id,
            label: this.externalTranTypeLabel.label,
            description: this.externalTranTypeLabel.description,
          }
        }

        this.createEditTransactionService
          .saveExternalMatch(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'External Transaction command successfully submitted.',
              )
              this.formSubmitting = false
              this.closeModal()
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      } else {
        tranCommand.notes =
          TransactionConstants.HELIOS_FX_TRADE_SAVE_NOTE_CW_CREATE
        this.createEditTransactionService
          .createTransaction(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'Clearwater Transaction create command successfully submitted.',
              )
              this.formSubmitting = false
              this.closeModal()
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      }
    }
  }

  prepareTransactionData(isValidate: boolean): FXSpot {
    let createdDate = new Date().toISOString()
    const fromUnits = this.rowNodeMap.get('fromUnits')?.data.value
    const toUnits = this.rowNodeMap.get('toUnits')?.data.value
    if (isEditModal(this.transInput) && this.originalTransaction?.createdDate) {
      createdDate = new Date(
        this.originalTransaction?.createdDate,
      ).toISOString()
    }
    const cp =
      this.counterPartyList.length > 0
        ? getCounterPartyFromName(
            this.rowNodeMap.get('counterParty')?.data.value,
            this.counterPartyList,
          )
        : getDefaultUnknownCounterParty()
    const transactionData: FXSpot = {
      securityId: getCurrencyFromCurrencyCode(
        this.rowNodeMap.get('toSecurityId')?.data.value,
        this.currencyList,
      )?.cashSecurityId as number,
      toSecurityId: getCurrencyFromCurrencyCode(
        this.rowNodeMap.get('toSecurityId')?.data.value,
        this.currencyList,
      )?.cashSecurityId,
      fromSecurityId: getCurrencyFromCurrencyCode(
        this.rowNodeMap.get('fromSecurityId')?.data.value,
        this.currencyList,
      )?.cashSecurityId,
      createdDate: createdDate,
      modifiedDate: new Date().toISOString(),
      active: true,
      userId: this.userId ?? 0,
      tranType:
        TransactionConstants.FX_SPOT_OPTIONS.find(
          op => op.label == this.rowNodeMap.get('tranType')?.data.value,
        )?.tranType ?? TransactionConstants.FX_SPOT_OPTIONS[0].tranType,
      sourceId: TransactionConstants.HELIOS_SOURCE_ID,
      accountId: this.accountId ?? 0,
      entryDate: this.rowNodeMap.get('entryDate')?.data.value,
      settleDate: this.rowNodeMap.get('settleDate')?.data.value,
      postDate: this.rowNodeMap.get('settleDate')?.data.value,
      counterParty: cp && cp.id != -1 ? cp : getDefaultUnknownCounterParty(),
      counterPartyId:
        cp && cp.id != -1 ? cp.id : getDefaultUnknownCounterParty().id,
      dataMap: this.originalTransaction?.dataMap,
      traderId: 0,
      revisionId: isEditModal(this.transInput)
        ? (this.originalTransaction?.revisionId ?? 0) + 1
        : 1,
    }
    if (!isValidate) {
      transactionData.sellToBuyRate = Math.abs(fromUnits / toUnits)
    }
    if (this.clearTransaction.value) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }
    if (fromUnits) {
      transactionData.fromUnits = isValidate
        ? Number(fromUnits)
        : -1 * fromUnits
      transactionData.units = isValidate ? Number(fromUnits) : -1 * fromUnits
    }
    if (toUnits) {
      transactionData.toUnits = Number(toUnits)
    }

    if (isEditModal(this.transInput)) {
      transactionData.units =
        isValidate && this.originalTransaction?.units
          ? Math.abs(this.originalTransaction?.units)
          : this.originalTransaction?.units
      transactionData.factor = this.originalTransaction?.factor
      transactionData.commission = this.originalTransaction?.commission
      transactionData.transactionId = this.originalTransaction?.transactionId
      transactionData.legacyOrigTranId =
        this.originalTransaction?.legacyOrigTranId
      transactionData.legacyTranId = this.originalTransaction?.legacyTranId
    }
    if (!isValidate && this.originalTransaction?.typeLabelId) {
      transactionData.typeLabelId = this.originalTransaction.typeLabelId
    }

    return transactionData
  }

  private closeModal() {
    this.activeModal.close()
  }

  getEditPromises(
    sessionId: string,
    transactionId: string,
    params: HttpParams,
  ): Promise<[TransactionDetail, Currency[]]> {
    return Promise.all([
      this.createEditTransactionService
        .loadTransactionDetail(
          sessionId,
          transactionId,
          params,
          TranCategoryUrl.FX_TRADE,
        )
        .toPromise(),
      this.createEditTransactionService.loadCurrencies().toPromise(),
    ])
  }

  getExternalPromises(
    extTranId: number,
    accountId: number,
  ): Promise<[ExternalTransactionDetail, Currency[]]> {
    return Promise.all([
      this.createEditTransactionService
        .loadExternalTransactionDetail(extTranId, accountId)
        .toPromise(),
      this.createEditTransactionService.loadCurrencies().toPromise(),
    ])
  }

  fetchSleeveSimples(accountId: number) {
    this.errorDetails = ''
    const sleeveSimples$ = this.createEditTransactionService
      .fetchSubAccounts(accountId)
      .pipe(retry(2))
    sleeveSimples$.subscribe(
      account => {
        this.sleeveSimpleAccount = account
        // Open the picker only once its options exist (opening earlier leaves the
        // select stuck loading). Skip when an external post already filled the account.
        if (
          this.aggregateAccountUnresolved &&
          this.gridApi?.api &&
          !this.isExternalAccountSleeveSimple
        ) {
          focusFieldRowEditor(this.gridApi.api, 'account')
        }
      },
      err => {
        this.errorHeader = TransactionConstants.ERROR_LOAD_SLEEVE_ACCOUNT
        this.errorDetails = err.error
      },
    )
  }

  async loadAggAccountDetails(accountDisplayName: string) {
    const account = this.sleeveSimpleAccount.find(
      name => name.displayLabel == accountDisplayName,
    )
    if (account) {
      await this.loadAndMapAccountDetailsForSleeve(account.id)
      // A concrete account is now resolved; clear the account-unresolved marks and validate.
      this.aggregateAccountUnresolved = false
      clearAllInvalidations(this.gridApi?.api)
      this.validate()
    }
  }

  setAccountDisplayName() {
    const accountRowField = this.rowData?.find(row => row.id == 'account')
    // Clear an aggregate account and force a sub-account pick, unless a sleeve-simple post
    // already resolved it to its concrete sub-account.
    if (
      this.accountDetails?.isAggregate &&
      !this.isExternalAccountSleeveSimple
    ) {
      if (accountRowField) {
        accountRowField.isEditable = true
        accountRowField.value = ''
        // Flag the account row red until a concrete sub-account is picked.
        accountRowField.validityStatus = 'invalid'
        accountRowField.errorMessage = ACCOUNT_REQUIRED_ERROR
      }
      this.aggregateAccountUnresolved = true
      this.gridApi?.api.refreshCells({ force: true })
      this.fetchSleeveSimples(this.accountDetails?.accountId)
      this.resetAggAccountDetails()
    } else {
      if (accountRowField) {
        accountRowField.value =
          this.accountDetails?.accountId +
          ' - ' +
          this.accountDetails?.accountName
      }
    }
  }

  resetAggAccountDetails() {
    this.accountId = undefined
    this.accountDetails = undefined
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  async loadAndMapAccountDetailsForSleeve(accountId: number) {
    this.accountDetails = await this.createEditTransactionService
      .loadAccountDetails(accountId)
      .toPromise()
    this.accountId = this.accountDetails?.accountId
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  getCounterPartyOptions() {
    if (this.counterPartyList.length < 2) {
      return {
        itemsAsync: this.createEditTransactionService.loadCounterParties(),
        bindValue: 'name',
        bindLabel: 'name',
      }
    } else {
      return {
        items: this.counterPartyList,
        bindValue: 'name',
        bindLabel: 'name',
      }
    }
  }
}
